import { MentorProfileIdentity, MentorProfilePutBody } from './MentorPortalService';
import { initialsFromName, joinPresent } from './mentorDashboardFormat';

type Props = {
    identity: MentorProfileIdentity;
    draft: MentorProfilePutBody;
    onChange: (patch: Partial<MentorProfilePutBody>) => void;
};

const MentorProfileDetails = ({ identity, draft, onChange }: Props) => {
    const name = identity.name || null;
    const identityLine = joinPresent([draft.designation, draft.currentCompany]);
    const batchLabel = identity.batchYear == null ? null : String(identity.batchYear);
    // Campus name is not on GET /mentor/profile (campusId only) — show —, do not invent a name.
    const campusLabel = '—';
    const experience =
        identity.experienceYears != null && Number.isFinite(identity.experienceYears) && identity.experienceYears > 0
            ? `${identity.experienceYears} yrs experience`
            : null;
    const photo = identity.photo && /^https?:\/\//i.test(identity.photo.trim()) ? identity.photo.trim() : null;

    return (
        <>
            <div className="col-md-6 col-lg-6">
                <div className="card border-0 shadow-sm rounded-3 bg-white h-100">
                    <div className="card-header bg-white border-bottom border-light-subtle p-3">
                        <h6 className="fw-bold mb-0 text-dark">Profile Details</h6>
                    </div>
                    <div className="card-body p-3">
                        <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom border-light-subtle">
                            {photo ? (
                                <img className="mprof-avatar-img" src={photo} alt="" />
                            ) : (
                                <span className="mdash-avatar mprof-avatar" aria-hidden="true">
                                    {initialsFromName(name)}
                                </span>
                            )}
                            <div>
                                {name ? <h6 className="fw-bold mb-0 text-dark">{name}</h6> : null}
                                {identityLine ? <div className="text-secondary small font-12 mt-1">{identityLine}</div> : null}

                                <div className="d-flex gap-2 mt-2">
                                    {batchLabel ? (
                                        <span className="badge bg-success bg-opacity-10 text-success font-11">{batchLabel}</span>
                                    ) : null}
                                    <span className="badge bg-primary bg-opacity-10 text-primary font-11">{campusLabel}</span>
                                    {experience ? (
                                        <span className="badge bg-primary bg-opacity-10 text-primary font-11">{experience}</span>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label font-13 font-600 text-secondary text-uppercase mb-1">
                                Mentor Bio (shown to mentees)
                            </label>

                            <textarea
                                className="form-control font-13"
                                rows={4}
                                value={draft.bio}
                                onChange={(event) => onChange({ bio: event.target.value })}
                            />
                        </div>

                        <div className="row g-2 mb-3">
                            <div className="col-md-6">
                                <label className="form-label font-13 font-600 text-secondary text-uppercase mb-1">Current Company</label>

                                <input
                                    className="form-control font-13"
                                    type="text"
                                    value={draft.currentCompany}
                                    onChange={(event) => onChange({ currentCompany: event.target.value })}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label font-13 font-600 text-secondary text-uppercase mb-1">Designation</label>
                                <input
                                    className="form-control font-13"
                                    type="text"
                                    value={draft.designation}
                                    onChange={(event) => onChange({ designation: event.target.value })}
                                />
                            </div>
                        </div>

                        <div className="row g-2 mb-0">
                            <div className="col-md-6">
                                <label className="form-label font-13 font-600 text-secondary text-uppercase mb-1">Location</label>
                                <input
                                    className="form-control font-13"
                                    type="text"
                                    value={draft.location}
                                    onChange={(event) => onChange({ location: event.target.value })}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label font-13 font-600 text-secondary text-uppercase mb-1">
                                    LinkedIn Profile URL <span className="text-danger">*</span>
                                </label>
                                <input
                                    className="form-control font-13"
                                    type="url"
                                    required
                                    value={draft.linkedinUrl}
                                    onChange={(event) => onChange({ linkedinUrl: event.target.value })}
                                    placeholder="https://www.linkedin.com/in/…"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MentorProfileDetails;
